import React, { lazy, useCallback, useEffect, useMemo, useRef, useState, Suspense } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { APISettingsModal } from '../../components/modals/APISettingsModal';
import { SettingsManager, DataLibrary } from '../world-building';
import { confirmLeaveSettingsDraft } from '../world-building/settingsDraft';
import { PlotThreads } from '../world-building/PlotThreads';
import { EditorContext, EditorView, DataTab, useEditorContext } from './context';
import { EditorSidebar } from './sidebar';
import { useGlobalSearch } from './GlobalSearch';
import { SidebarRail } from './SidebarRail';
import { PaneRail } from './PaneRail';
import { ResidentPanel } from './ResidentPanel';
import { ResizeHandle } from './ResizeHandle';
import { usePaneLayout, type PaneLayoutApi } from './usePaneLayout';
import {
  AI_PANE, MIN_PANE_PX, MIN_SIDEBAR_PX, PANE_ANIM_MS, PANE_LABEL, PANE_ORDER,
  computeDefaultWidths, fitColumnWidths, isDataPaneId, isPaneId, type DataPaneId, type PaneId,
} from './paneLayout';
import { WorkspaceProvider } from '../workspace/WorkspaceContext';
import { GlobalConfirmModal } from '../../components/modals/GlobalConfirmModal';
import { isHarnessTurnActive } from '../../ai/harnessAgent';
import { Workspace } from '../workspace';
import { AIChatProvider } from '../ai-chat';
import { EditorAIPanel } from './EditorAIPanel';
import { currentLayoutWidth, DESIGN_WIDTH, getAutoUiScaleEnabled, isSatelliteWindow } from '../../utils/uiScale';
import { isDesktopHost } from '../../utils/host';
import {
  closePaneWindow, dockSatellite, fetchWindowExpanded, listPaneWindows, openPaneWindow, sizeMainWindow,
} from '../../utils/desktopWindows';
import { usePaneEnterWidth } from './usePaneEnterWidth';
import { SoloPaneWindow } from './SoloPaneWindow';

import './styles/editor.css';

const AIChatContent = lazy(() => import('../ai-chat/AIChatContent'));
const VersionHistoryPage = lazy(() => import('../workspace/components/VersionHistoryPage'));

export { useEditorContext } from './context';

/**
 * 本进程是否已经给**原生主窗口**定过尺寸。
 *
 * 刻意用**模块级**（不是 `useRef`）：路由切换会重新挂载创作页，而"主页 → 创作页"时
 * 再设一次窗口宽度会让窗口被拉长（用户实测反馈："没调窗口大小，从主页进创作页后窗口
 * 被意外拉长"）。模块级标志随页面模块常驻，天然就是"每个进程一次"。
 * 「重置页面宽度」是用户显式动作，不受这个标志约束（见 `resetWidths`）。
 */
let MAIN_WINDOW_SIZED = false;

export function Editor() {
  const navigate = useNavigate();
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const stored = localStorage.getItem('dark-mode');
    const isDark = stored !== null ? stored === '1' : document.documentElement.classList.contains('dark-mode');
    if (isDark) document.documentElement.classList.add('dark-mode');
    else document.documentElement.classList.remove('dark-mode');
    return isDark;
  });
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isEntering, setIsEntering] = useState(true);
  const [isExiting, setIsExiting] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);

  const [activeView, setActiveViewState] = useState<EditorView>('chapters');
  const [expandedMenus, setExpandedMenus] = useState<Record<EditorView, boolean>>({
    chapters: true, 'ai-chat': false, 'version-history': false,
  });
  // 注：settingsTab 已移除 —— 设定管理只剩「作品设定」单页
  const [dataTab, setDataTab] = useState<DataTab>('factions');

  const paneLayout = usePaneLayout();

  /**
   * 独立窗口模式：桌面端把某一页开成独立窗口时，Rust 会让那个窗口加载
   * `#/editor/<bookId>?solo=<页>`（见 windowhost.rs）。这种窗口只渲染那一页内容，
   * 没有侧栏、编辑区与图标栏 —— 窗口边框由操作系统提供（"不限制吸附显示"）。
   *
   * 只在桌面端生效：网页端即使带上这个参数也照常渲染完整创作页，
   * 避免"分享一个带参数的链接就变成半截界面"。
   */
  const [searchParams] = useSearchParams();
  const soloParam = searchParams.get('solo');
  const soloPane = isDesktopHost() && soloParam && (PANE_ORDER as readonly string[]).includes(soloParam)
    ? (soloParam as PaneId)
    : null;

  /**
   * 副窗口模式：桌面端把某个面板开成"贴主窗口右侧"的独立窗口时，
   * Rust 会让那个窗口加载 #/editor/<bookId>?solo=<面板>（见 windowhost.rs）。
   * 这种窗口只渲染那一块面板 + 一条细标题栏，没有侧栏与编辑区。
   *
   * 只在桌面端生效：网页端即使带上这个参数也照常渲染完整创作页，
   * 避免"分享一个带参数的链接就变成半截界面"。
   */


  const setMenuExpanded = (view: EditorView, expanded: boolean) =>
    setExpandedMenus(prev => ({ ...prev, [view]: expanded }));
  const toggleMenu = (view: EditorView) =>
    setExpandedMenus(prev => ({ ...prev, [view]: !prev[view] }));
  const setActiveView = useCallback((view: EditorView) => {
    setActiveViewState(view);
    setExpandedMenus(prev => (prev[view] ? prev : { ...prev, [view]: true }));
  }, []);

  /**
   * AI 会话还在进行时，先确认再离开作品。
   *
   * 服务端那个回合**不会**因为你离开作品而中止：它继续生成（继续花 token），
   * 需要审批时还会停在那里等你（审批不设超时）。所以要如实拦一下 ——
   * 否则用户会以为"返回书架"等于取消。
   *
   * 用 `isHarnessTurnActive()`（harnessAgent 的同步函数）而不是状态：
   * 这个按钮在 WorkspaceProvider **外面**，拿不到面板内部状态。
   */
  const [exitConfirmOpen, setExitConfirmOpen] = useState(false);

  const doNavigateHome = () => {
    setIsExiting(true);
    setTimeout(() => navigate('/home'), 300);
  };

  /**
   * 回书架之前，先处理「设定管理」里可能还没保存的改动。
   *
   * 设定正文按用户要求**不自动保存**，改动只活在面板的组件状态里 —— 直接导航就会静默丢掉。
   * 面板自己（`WorldSettings`）注册了守卫，这里只是"问一句"：
   * 面板在**本窗口**内时弹窗由它渲染；桌面端面板可能正开在吸附窗口里
   * （另一个 SPA 实例），`remote` 让它把这一问转达过去（见 `world-building/settingsDraft.ts`）。
   *
   * 先问设定、再问 AI 会话：两个确认框叠在一起没人看得懂，顺序问最清楚。
   */
  const handleNavigateHome = () => {
    const settingsPaneOpen = paneLayout.layout.dataPane === 'settings';
    void confirmLeaveSettingsDraft('leave-page', { remote: isDesktopHost() && settingsPaneOpen })
      .then((allowed) => {
        if (!allowed) return;
        if (isHarnessTurnActive()) {
          setExitConfirmOpen(true);
          return;
        }
        doNavigateHome();
      });
  };

  useEffect(() => {
    setIsEntering(true);
    const timer = setTimeout(() => setIsEntering(false), 300);
    return () => clearTimeout(timer);
  }, []);

  useEffect(() => {
    if (isDarkMode) document.documentElement.classList.add('dark-mode');
    else document.documentElement.classList.remove('dark-mode');
  }, [isDarkMode]);

  const toggleDarkMode = () => {
    const nextMode = !isDarkMode;
    setIsDarkMode(nextMode);
    if (nextMode) document.documentElement.classList.add('dark-mode');
    else document.documentElement.classList.remove('dark-mode');
    localStorage.setItem('dark-mode', nextMode ? '1' : '0');
  };

  const { toggle, show } = paneLayout;

  /**
   * 「某页此刻是否已经可见」的只读判定，交给子组件（如 AI 助手）判断要不要发提醒。
   * 判据直接复用 `visibleColumns` —— 与 EditorLayout 里 `<section hidden={!isShown}>`
   * 用的是同一份状态，两边不会各说各话。
   *
   * 这里用**可见性签名字符串**做 memo 依赖（而不是 `layout` 对象）：
   * 拖拽调宽每帧都会产生新 `layout`，若依赖它，`isPaneVisible` 每帧换一个新引用，
   * context 值随之每帧变化 —— 所有 `useEditorContext()` 的消费者（含 WritingMode）
   * 都会跟着每帧重渲染。签名只在"哪些页可见"真正变化时才变，
   * 于是拖宽度不会牵动任何消费者，也不会每帧重建 AI 的回合回调。
   */
    // 两个槽位各自可能开着（AI 在左、数据页在右），签名就是"哪几页可见"
    const visiblePaneKey = [paneLayout.layout.aiOpen ? AI_PANE : '', paneLayout.layout.dataPane ?? ''].join(',');
  const isPaneVisible = useCallback(
    (id: PaneId) => visiblePaneKey.split(',').includes(id),
    [visiblePaneKey],
  );

  // Ctrl+K 开关 AI 助手面板；Ctrl+Shift+F 派发到全局搜索。
  // 判据用 `e.code` 而不是 `e.key`：带 Shift 时 `e.key` 是**大写** `'F'`，
  // 原先写 `e.key === 'f'` 导致「全书检索 Ctrl+Shift+F」恒不触发（工具栏提示却写着它）。
  // 同时排除 Alt，避免和 Alt+Ctrl+F 这类系统/输入法组合抢键。
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.metaKey || e.ctrlKey) || e.altKey) return;
      if (e.code === 'KeyK') {
        e.preventDefault();
        toggle('ai');
      } else if (e.code === 'KeyF' && e.shiftKey) {
        e.preventDefault();
        window.dispatchEvent(new Event('xiaotianshu:focus-search'));
      }
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);

  const contextValue = useMemo(() => ({
    activeView, setActiveView,
    expandedMenus, setMenuExpanded, toggleMenu,
    dataTab, setDataTab,
    showPane: show,
    isPaneVisible,
  }), [activeView, setActiveView, expandedMenus, dataTab, show, isPaneVisible]);

  return (
    <EditorContext.Provider value={contextValue}>
      <WorkspaceProvider>
        <AIChatProvider>
          {soloPane ? (
            <SoloPaneWindow pane={soloPane} />
          ) : (
          <EditorLayout
            isEntering={isEntering}
            isExiting={isExiting}
            isDarkMode={isDarkMode}
            onToggleDarkMode={toggleDarkMode}
            sidebarCollapsed={sidebarCollapsed}
            onToggleSidebar={() => setSidebarCollapsed(prev => !prev)}
            showSettingsModal={showSettingsModal}
            setShowSettingsModal={setShowSettingsModal}
            handleNavigateHome={handleNavigateHome}
            paneLayout={paneLayout}
          />
          )}
        </AIChatProvider>
      </WorkspaceProvider>

      {/*
        AI 会话进行中想返回书架 → 如实说明"离开不等于取消"，把决定权交给用户。
        挂在这一层（而不是 EditorLayout 里）：状态与 handleNavigateHome 都在 Editor 作用域。
      */}
      <GlobalConfirmModal
        show={exitConfirmOpen}
        title="AI 会话还在进行"
        message="当前 AI 会话还没结束（可能正在生成，或正等你确认一项改动）。离开作品不会取消它：服务端会继续运行并一直等你回来，本次生成的费用也会照常产生。确定要离开吗？"
        confirmText="仍然离开"
        cancelText="留在这里"
        onConfirm={doNavigateHome}
        onCancel={() => setExitConfirmOpen(false)}
      />
    </EditorContext.Provider>
  );
}

function EditorLayout({
  isEntering,
  isExiting,
  isDarkMode,
  onToggleDarkMode,
  sidebarCollapsed,
  onToggleSidebar,
  showSettingsModal,
  setShowSettingsModal,
  handleNavigateHome,
  paneLayout,
}: {
  isEntering: boolean;
  isExiting: boolean;
  isDarkMode: boolean;
  onToggleDarkMode: () => void;
  sidebarCollapsed: boolean;
  onToggleSidebar: () => void;
  showSettingsModal: boolean;
  setShowSettingsModal: (show: boolean) => void;
  handleNavigateHome: () => void;
  paneLayout: PaneLayoutApi;
}) {
  const { activeView, setActiveView, dataTab } = useEditorContext();
  /**
   * 全局搜索实例**在这里持有**（而不是在侧栏里）：侧栏的搜索框与 AI 对话视图内的
   * 会话列表过滤要用**同一份**状态。两处各自调 `useGlobalSearch()` 会得到两个
   * 互不相干的状态，搜索框输入的过滤传不到视图里。
   */
  const search = useGlobalSearch();
  const convFilterIds = search.filterActive && search.match ? search.match.conversationIds : null;

  /**
   * AI 对话视图是否**曾经打开过**。打开过就常驻（只切 `hidden`），
   * 否则切回章节会把组件卸载掉、正在流式生成的回复直接断掉。
   */
  const [chatEverOpened, setChatEverOpened] = useState(false);
  useEffect(() => {
    if (activeView === 'ai-chat') setChatEverOpened(true);
  }, [activeView]);
  const {
      layout, setSidebarWidth, resizeSidebarBy,
      setPaneWidth, resizePaneBy, toggle, setDataPane, rememberMainWidth, applyWidths,
  } = paneLayout;

  /**
   * 当前视口宽度（防抖 120ms）—— 用来算"有效宽度"。
   *
   * 桌面端窗口可以被拖动、网页端窗口可以缩放，所以不能只在启动时算一次；
   * 但也不必每帧都算（拖窗口时很密集），120ms 足够跟手。
   *
   * ⚠️ 声明位置必须在**用到它的地方之前**（入场动画的目标宽度也用它，见下面的 `fit.pane`）。
   */
  const [layoutWidth, setLayoutWidth] = useState(() => currentLayoutWidth());
  useEffect(() => {
    // 同步更新（不防抖）：与全局缩放保持同一时刻的值，否则松手后会「跳」一下
    // （同一个坑在顶部工具栏上表现为换行数变化，见 App.tsx 的说明）。
    // 量化到 8px：值不变时 React 跳过重渲染，拖窗口不会每帧重渲染整个创作页。
    const onResize = () => setLayoutWidth(currentLayoutWidth());
    window.addEventListener('resize', onResize);
    return () => { window.removeEventListener('resize', onResize); };
  }, []);

  /**
   * 主窗口是否**最大化 / 全屏**（只有桌面端会变）。
   *
   * 用户提出的场景：桌面端想要"用满整个屏幕"时，副窗口反而碍事 ——
   * 所以**最大化/全屏时不弹副窗口**，改成像网页端那样把数据页显示在主窗口内。
   *
   * 判定交给 Rust（见 `desktopWindows.fetchWindowExpanded`），这里只在窗口尺寸变化时重查一次
   * （最大化/还原必然伴随 resize）。
   */
  const [windowExpanded, setWindowExpanded] = useState(false);
  useEffect(() => {
    if (!isDesktopHost()) return;
    let timer: number | undefined;
    const refresh = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        void fetchWindowExpanded().then((state) => { if (state) setWindowExpanded(state.expanded); });
      }, 150);
    };
    refresh();
    window.addEventListener('resize', refresh);
    return () => { window.clearTimeout(timer); window.removeEventListener('resize', refresh); };
  }, []);

  /**
   * **数据页渲染在窗口内吗**（第六版模型的关键分叉）。
   *
   * - 网页端：没有窗口系统 → 数据页就在窗口内，排在 AI 右边（编辑区 | AI | 数据页）；
   * - 桌面端：默认进**吸附窗口**，窗口内只有 AI（见 `togglePaneById`）；
   *   **但主窗口最大化/全屏时改为在窗口内显示**（用户要求：用满屏时别再多一个窗口）。
   *
   * 两端的"当前打开的是哪个数据页"是同一个字段 `dataPane`。
   *
   * ⚠️ 声明必须在用到它的地方（下面的 `fit`）**之前** —— 它参与"窗口内还剩多少宽度"的计算：
   * 桌面端的数据页不在窗口内时算进去，会把 AI 面板压窄并隐藏它的拖拽手柄（用户实测的 bug）。
   */
  const dataInWindow = !isDesktopHost() || windowExpanded || layout.dataPane === 'resident';

  /**
   * 按当前视口算出的**有效宽度**（侧栏 / 面板）——不覆盖用户偏好，只在挤不下时往下压，
   * 窗口变大自动恢复。规则与理由见 `paneLayout.fitColumnWidths`。
   */
  const fit = useMemo(
    // 传的是**布局空间**宽度（已除以 zoom），与 `fitColumnWidths` 的坐标系一致。
    // ⚠️ 第三个参数必须如实传：桌面端数据页在**吸附窗口**里，不占主窗口宽度 ——
    //    把它算进来会把 AI 面板压窄，并连带隐藏它的拖拽手柄（用户实测：
    //    "副窗口打开时 AI 助手面板宽度突然变窄、且无法调节"）。
    () => fitColumnWidths(layout, layoutWidth, dataInWindow),
    [layout, layoutWidth, dataInWindow],
  );

  /**
   * 面板被视口压窄了 → **不显示拖拽手柄**。
   * 拖了也看不出变化，留着只会让人以为"手柄坏了"（与窄屏下隐藏侧栏手柄同一个理由）。
   */
  const paneWidthClamped = fit.pane < layout.paneWidth;

  /**
   * 面板内容 —— **四个页各自一个实例**，挂载策略由外层决定：
   * - AI 助手一旦打开过就**常驻**（切走只是宽度归零，不卸载）；
   * - 另外三页是数据视图，关掉就卸载（下次打开重新拉数据）。
   */
  const paneBody: Record<PaneId, React.ReactNode> = useMemo(() => ({
    settings: <SettingsManager />,
    data: <DataLibrary initialTab={dataTab} />,
    threads: <PlotThreads />,
    resident: <ResidentPanel />,
    ai: <EditorAIPanel />,
  }), [dataTab]);

  const aiOpen = layout.aiOpen;
  const dataPane = layout.dataPane;


  /** AI 助手：第一次打开之后就一直挂载（只隐藏，不卸载） */
  const [aiMounted, setAiMounted] = useState(false);
  useEffect(() => {
    if (aiOpen) setAiMounted(true);
  }, [aiOpen]);

  /**
   * 数据页的**退场**：关闭后仍继续渲染上一页 `PANE_ANIM_MS`，等宽度过渡到 0 再卸载 ——
   * 否则面板瞬间消失。
   *
   * ⚠️ 这段状态必须在**渲染期同步**跟出来（React 的 "adjusting state during render" 模式），
   * **不能放进 `useEffect`**：effect 比渲染晚一帧，而那一帧 `dataPane` 已经是 null，
   * 于是 section 会被当场卸载、宽度过渡根本没有机会发生 ——
   * 现象就是"展开正常、收起瞬间消失"（用户实测反馈，我第一版就是栽在这里）。
   * 渲染期 `setState` 会让 React 立刻用新状态重跑一次渲染再提交，
   * 所以提交出去的那一帧里 section 仍在、宽度才有起点。
   *
   * 只对数据页做：AI 是从不卸载的（宽度归零即可），三个数据页关掉就是真关掉
   * （下次打开重新拉数据）。
   */
  const [exitDataPane, setExitDataPane] = useState<DataPaneId | null>(null);
  const [lastDataPane, setLastDataPane] = useState<DataPaneId | null>(dataPane);
  if (lastDataPane !== dataPane) {
    setLastDataPane(dataPane);
    // 关掉 → 留住上一页走退场动画；打开/切页 → 直接显示它（原位换内容，不重放动画）
    setExitDataPane(dataPane ? null : lastDataPane);
  }

  /** 退场期间仍然渲染"上一页"，宽度由 CSS 过渡收回去 */
  const renderedDataPane = dataPane ?? exitDataPane;
  const dataVisible = Boolean(dataPane);

  // 退场计时：过渡走完才真正卸载（数据页是数据视图，关掉就是真关掉）
  useEffect(() => {
    if (!exitDataPane) return;
    const timer = window.setTimeout(() => setExitDataPane(null), PANE_ANIM_MS);
    return () => window.clearTimeout(timer);
  }, [exitDataPane]);

  /**
   * 面板宽度的**入场动画**：新打开的那一槽从 0 长到目标宽度。
   *
   * 两槽各用一份，于是"从右向左伸出"是自然结果：数据页从窗口右缘长出；
   * 数据页开着时再开 AI，AI 从**数据页左边**继续向左长出（编辑区被挤窄，窗口宽度不变）。
   *
   * ⚠️ 数据页的 key 用常量 `'data'` 而不是具体页名：切页要求**原位换内容**（不重放动画）。
   * AI 收起后仍常驻，所以它那份 hook 只管宽度。
   *
   * ⚠️ **CSS 宽度必须直接用 hook 的值，不能在"收起"时硬编码 0**（踩过）：
   * 硬编码 0 会让宽度在关闭那一帧直接跳到底，CSS 过渡无从发生 ——
   * 表现就是"展开正常、收起瞬间消失"（用户实测反馈）。
   * hook 在 key 变回 null 时会自己把宽度设为 0，那一步**是走过渡的**：
   * 数据页的 section 还会多留 `PANE_ANIM_MS` 才卸载（`exitDataPane`），正好把这段动画走完。
   * 目标宽度传的是 **`fit.pane`（有效宽度）而不是 `layout.paneWidth`（存储偏好）**：
   * 视口挤不下时真正参与布局的是前者；传错的话 section 会照样涨到存储宽度、把编辑区挤扁
   * （内层 `.ws-pane-fixed` 也跟着错位）。开关仍由 key 决定。
   */
  const aiEnterWidth = usePaneEnterWidth(aiOpen ? AI_PANE : null, fit.pane);
  const dataEnterWidth = usePaneEnterWidth(dataVisible ? 'data' : null, fit.pane);
  const aiCssWidth = aiEnterWidth;
  const dataCssWidth = dataEnterWidth;

  /**
   * **吸附窗口**里现在显示的是哪一页（桌面端）。
   *
   * 它就是 `layout.dataPane` —— 与网页端共用同一个字段，只是渲染位置不同。
   * 三个来源保证它不会跑偏：
   * - 打开/切换/收起由本组件直接改（`togglePaneById`）；
   * - 用户从标题栏关掉吸附窗口时，Rust 会往主窗口注入 `xts:pane-window-closed`
   *   （见 windowhost.rs），这里监听并清掉高亮；
   * - 主窗口（重新）加载时问一次 `list-panes`，于是"刷新过主窗口、但吸附窗口还开着"也能对上。
   */
  const [satellitePane, setSatellitePane] = useState<PaneId | null>(null);

  /**
   * **离开创作页就关掉副窗口**（用户要求：返回作品列表时不要留下孤儿副窗口）。
   *
   * 用卸载清理来覆盖"任何离开创作页的路径"（返回书架、进阅读器…），
   * 比在每个导航入口点各写一次更可靠。
   *
   * ⚠️ 只在**主窗口**做：副窗口自己也是一个 `?solo=` 的创作页路由，它的卸载不该反过来
   * 去关自己（`__XTS_PANE__` 是 Rust 只注入给副窗口的标记，见 windowhost.rs）。
   */
  useEffect(() => {
    if (!isDesktopHost()) return;
    if ((window as unknown as { __XTS_PANE__?: string }).__XTS_PANE__) return;
    return () => { void closePaneWindow(''); };
  }, []);
  /**
   * 吸附窗口在主窗口哪一侧（布局切换按钮改它）。
   *
   * 只是**本次会话的视图偏好**，不落盘：每次启动回到默认的右侧。
   */
  const [satelliteOnLeft, setSatelliteOnLeft] = useState(false);
  useEffect(() => {
    if (!isDesktopHost()) return;
    let alive = true;
    void listPaneWindows().then((panes) => {
      const pane = panes.find(isDataPaneId);
      if (alive && pane) setDataPane(pane);
    });
    const onClosed = () => setDataPane(null);
    window.addEventListener('xts:pane-window-closed', onClosed);
    return () => { alive = false; window.removeEventListener('xts:pane-window-closed', onClosed); };
  }, [setDataPane]);

  /**
   * 点图标栏：分派到两个去处（两个槽位互不影响）。
   *
   * - **AI 创作助手** → 窗口内那一槽（永远在数据页左侧）；
   * - **三个数据页** → 网页端是窗口内的另一槽（AI 右边，从右缘伸出）；
   *   桌面端是**吸附窗口**：已开着同一页则收起，开着别页则换它的内容（窗口不重建），
   *   没开则新建并吸附。
   */
  const togglePaneByIdNow = useCallback((pane: PaneId) => {
    // 余白是“聊天 + 想法/草案”双栏，太窄会两边都难读；首次打开给它一个可用的宽度。
    if (pane === 'resident' && layout.dataPane !== 'resident' && layout.paneWidth < 560) {
      setPaneWidth(600);
    }
    // AI 只在窗口内的左槽；网页端没有窗口系统，三个数据页也用窗口内的右槽；
    // 桌面端**最大化/全屏**时同样走窗口内（用户要求：用满屏时别再弹出副窗口）
    if (pane === AI_PANE || pane === 'resident' || !isDesktopHost() || dataInWindow) {
      toggle(pane);
      return;
    }
    // 走到这里 pane 一定是数据页（上面已排除 AI）
    if (dataPane === pane) {
      setDataPane(null);
      void closePaneWindow(pane);
      return;
    }
    // 先乐观地把高亮切过去（开窗要等 Rust 建窗/导航回来，几十到几百毫秒），
    // 失败再回滚 —— 否则点了按钮半天没反应，像是没生效。
    const previous = dataPane;
    setDataPane(pane);
    void openPaneWindow(pane, satelliteOnLeft, layout.satelliteWidth).then((result) => {
      if (!result) setDataPane(previous);
    });
  }, [toggle, dataInWindow, dataPane, setDataPane, satelliteOnLeft, layout.satelliteWidth, layout.dataPane, layout.paneWidth, setPaneWidth]);

  /**
   * 图标栏的点击入口：**「设定管理」要离开之前先问一句"保存了吗"**，其余原样转发。
   *
   * 两种"离开"都覆盖：切到别的数据页（原位换内容）、再点一次同一页（收起）。
   * 设定正文不自动保存，这两条路都会把面板卸载掉（吸附窗口那条路则是换掉它的内容），
   * 不问就是静默丢改动。
   *
   * 面板在**吸附窗口**里时（桌面端且窗口未最大化），本窗口没有面板实例，
   * `remote` 让这一问转达到那个窗口 —— 弹窗必须出现在用户正在编辑的地方。
   * 用户在那边选了「取消」就什么都不做（高亮、Rust 的窗口标题都不会被改），
   * 因为开窗/换页是**等回答之后**才发生的。
   */
  const togglePaneById = useCallback((pane: PaneId) => {
    // 只有「设定管理」离开时才需要确认；开/关 AI 不影响它，原样转发
    const settingsLeaving = dataPane === 'settings' && pane !== AI_PANE;
    if (!settingsLeaving) {
      togglePaneByIdNow(pane);
      return;
    }
    // 再点一次同一页 = 收起；点别的数据页 = 换内容，两者都是"离开设定管理"
    const reason = pane === 'settings' ? 'close-pane' : 'switch';
    void confirmLeaveSettingsDraft(reason, { remote: isDesktopHost() })
      .then((allowed) => { if (allowed) togglePaneByIdNow(pane); });
  }, [dataPane, togglePaneByIdNow]);

  /**
   * **最大化/还原会切换"数据页放哪儿"** —— 把已经打开的那一页搬过去，别让它消失。
   *
   * - 变成"窗口内"（最大化/全屏）→ 关掉副窗口，数据页改在主窗口里显示（像网页端）；
   * - 变回"副窗口"（还原）→ 重新开副窗口。
   *
   * 只在**模式变化**时跑（依赖只有 `dataInWindow`）：窗口每动一次像素都重开窗口是灾难。
   * 其余用到的值都从本次渲染的闭包里取，正是切换那一刻的最新值。
   */
  useEffect(() => {
    if (!isDesktopHost() || dataPane === null || dataPane === 'resident') return;
    // ⚠️ 副窗体自己也是一个创作页路由：它不该去开关窗口（那包括关掉它自己）
    if (isSatelliteWindow()) return;
    /**
     * ⚠️ 这一步会**把面板连人带草稿搬走**（吸附窗口 ↔ 主窗口），所以先让面板把未保存的
     * 设定改动处理掉。用 `finally`：窗口状态已经变了，这一搬是不可取消的 ——
     * 用户在确认框里选「取消」也只意味着"别保存"，而不是"别还原窗口"。
     * 方向决定问谁：要搬进主窗口 → 面板此刻在吸附窗口里（`remote`）；反之在本窗口内。
     * 当前不是「设定管理」时直接跳过：同一时刻只可能开着一页数据面板，那一页里没有设定草稿。
     */
    const inWindow = dataInWindow;
    const settled = dataPane === 'settings'
      ? confirmLeaveSettingsDraft('pane-move', { remote: inWindow })
      : Promise.resolve(true);
    void settled.finally(() => {
      if (inWindow) void closePaneWindow(dataPane);
      else void openPaneWindow(dataPane, satelliteOnLeft, layout.satelliteWidth);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataInWindow]);


  /**
   * **宽度约定的落地**（用户要求）：
   *
   * - **首次打开软件**（或旧数据里没有宽度配置）→ 按屏幕档位算一组默认值
   *   （侧栏 / 面板宽度随档位收窄，见 `TIER_WIDTHS`），桌面端还要把它应用到原生窗口；
   * - 之后每次启动 → 直接用**记录下来的**宽度（用户调过就用他的值，不会被覆盖）。
   *
   * 两端都要跑：网页端虽然不用设原生窗口，但"首次启动的默认宽度"同样要按**当前视口**
   * 算一次（14 寸笔记本上按 1920 大屏的默认值起步，一开面板就挤扁 —— 用户实测反馈）。
   *
   * 两个"只做一次"的守卫，作用域**不同**，缺一不可：
   *   · `widthsApplied`（`useRef`，按**组件实例**）—— 同一个实例内不要反复跑；
   *   · `MAIN_WINDOW_SIZED`（模块级，按**进程**）—— **原生窗口的尺寸整个进程只设一次**。
   *
   * ⚠️ 后者是修 bug 加上去的（用户实测："没调窗口大小，从主页进创作页后窗口被意外拉长"）：
   * `useRef` 只对当前实例有效，而路由切换会**重新挂载**创作页 —— effect 再跑一次，
   * 于是又把窗口设成 `mainWidth`（屏幕的约 92%），表现就是"一进创作页窗口就变长"。
   */
  const widthsApplied = useRef(false);
  useEffect(() => {
    if (widthsApplied.current) return;
    widthsApplied.current = true;
    // 桌面端看屏幕（窗口约等于屏幕），网页端看视口
    const screenWidth = isDesktopHost()
      ? (window.screen?.availWidth || window.innerWidth || 1440)
      : (window.innerWidth || 1440);
    /**
     * 侧栏/面板默认值的"分档基准"。
     *
     * 这两个宽度活在**布局空间**里（`#root` 带 zoom），而 `screenWidth` 是屏幕像素 ——
     * 开了"随窗口自动缩放"时布局空间恒为设计尺寸，所以基准取设计宽度：
     * 于是**任何屏幕上的默认版面都一致**（用户要的"比例相同则内容相同"）。
     * 关掉自动缩放时两者是同一个坐标系，退化为按屏幕分档。
     */
    const tierBasis = getAutoUiScaleEnabled() ? DESIGN_WIDTH : screenWidth;

    if (!isDesktopHost()) {
      if (!layout.widthsReady) applyWidths(screenWidth, tierBasis);
      return;
    }

    /**
     * ⚠️ **副窗体（`?solo=`）到此为止**：它也会挂载创作页（整窗就是那一页），但
     * "给主窗口定尺寸"和"写首次启动的默认宽度"都是**主窗口**的事 ——
     * 副窗体做这两件事只会篡改主窗口的窗口大小与共享的 localStorage 布局。
     */
    if (isSatelliteWindow()) return;

    // 存储里的宽度若是明显不合理的坏值（早先版本累积出来的 4260 之类），按屏幕重算。
    const sane = layout.widthsReady && layout.mainWidth >= 640 && layout.mainWidth <= screenWidth;

    /**
     * ⚠️ 正常情况**不要动原生窗口**（用户实测两轮："从主页进创作页时窗口被意外拉长/变宽"）。
     *
     * 两个理由：
     *  1. 窗口的位置与尺寸由 `tauri-plugin-window-state` 恢复 —— 前端再设一次只会跟它打架；
     *  2. **`mainWidth` 不是"窗口宽度"**：它是在创作页里量出来的（开着 AI 面板时还会扣掉
     *     面板宽度），而用户在主页/书架页调整窗口时它根本不会更新。拿一个陈旧值去设窗口，
     *     表现就是"一进创作页窗口就变了"。
     *
     * 所以只有"首次启动（没有任何记录）"或"记录明显不合理"时才设一次，
     * 而且用**当场算出来的默认值**，绝不用 `mainWidth`。
     */
    if (sane || MAIN_WINDOW_SIZED) return;
    MAIN_WINDOW_SIZED = true;
    const defaults = computeDefaultWidths(screenWidth, tierBasis);
    applyWidths(screenWidth, tierBasis);
    void sizeMainWindow(defaults.main);
  }, [layout.widthsReady, layout.mainWidth, applyWidths]);

  /**
   * 记录用户对**基础宽度**的调整（拖窗口边缘）。
   *
   * 页面自己的 `innerWidth` 就是主窗口内容宽度（CSS 像素），不必让原生侧回报。
   * ⚠️ 记之前要**减去助手面板那一块**：窗口现在宽，是因为助手开着，
   * 不该把这个宽度记成"用户想要的宽度"—— 否则下次启动从一个被撑大的宽度开始（越用越宽）。
   * 防抖 400ms：拖拽时每帧都会 resize，不防抖会写爆 localStorage。
   */
  useEffect(() => {
    if (!isDesktopHost()) return;
    let timer: number | undefined;
    const onResize = () => {
      window.clearTimeout(timer);
      timer = window.setTimeout(() => {
        /**
         * 记录用户对**主窗体宽度**的调整（拖窗口边缘）。
         *
         * 面板不再让窗口变宽了（用户要求：面板在窗口内从右侧向左伸出），
         * 所以这里就是窗口自己的内容宽度，不需要再减去面板那一块。
         * 防抖 400ms：拖拽时每帧都会 resize，不防抖会写爆 localStorage。
         */
        const base = window.innerWidth;
        rememberMainWidth(base);
      }, 400);
    };
    window.addEventListener('resize', onResize);
    return () => { window.clearTimeout(timer); window.removeEventListener('resize', onResize); };
  }, [rememberMainWidth]);

  /** 设置页的「重置页面宽度」：按当前屏幕重算三项并立即应用（副窗口开着就让它重摆一次） */
  const resetWidths = useCallback(() => {
    const screenWidth = window.screen?.availWidth || window.innerWidth || 1440;
    // 与首次启动同一套规则：开了自动缩放时，分档基准取设计宽度（见上面的说明）
    const tierBasis = getAutoUiScaleEnabled() ? DESIGN_WIDTH : screenWidth;
    const next = computeDefaultWidths(screenWidth, tierBasis);
    applyWidths(screenWidth, tierBasis);
    // 同理：重置后基线就是新的基础宽度（助手此刻若开着，目标另算）
    if (isDesktopHost()) {
      void sizeMainWindow(next.main);
      void dockSatellite(satelliteOnLeft);
    }
  }, [applyWidths, satelliteOnLeft]);

  /**
   * 切换布局：把吸附窗口挪到主窗口另一侧。
   *
   * 两件事一起做：改状态（图标随之变）**并且**让那个窗口真的挪过去 ——
   * 后者交给 Rust（位置由它算，它也知道当前摆在哪一侧）。窗口没开时
   * setState 只记下偏好，下次打开就按这一侧摆。
   */
  const swapSatelliteSide = useCallback(() => {
    setSatelliteOnLeft((prev) => {
      const next = !prev;
      void dockSatellite(next);
      return next;
    });
  }, []);

  return (
    <div
      className="editor-root"
      style={{
        animation: isEntering ? 'fadeUp 0.3s ease-out both' : (isExiting ? 'fadeOut 0.2s ease-in forwards' : 'none'),
        // 宽度一律写成**真实像素**（收起也保留原值）：
        // 收起是由 margin-left 负偏移实现的，若这里写 0 会让过渡退化成"一帧消失"。
        //
        // ⚠️ 两个变量分工不同（别合并）：
        //   `--sidebar-width`   用户拖出来的**偏好值**（存储里的原值，拖手柄时改它）
        //   `--sidebar-effective` 当前视口下**实际参与布局**的值（挤不下时会变小）
        // 与 CSS 里窄屏媒体查询的 `min(var(--sidebar-width), 32vw)` 是同一套思路，
        // 只是这里由 JS 统一算（面板宽度也要一起算，见 fitColumnWidths）。
        '--sidebar-width': `${layout.sidebar}px`,
        '--sidebar-effective': `${fit.sidebar}px`,
      } as React.CSSProperties}
    >
      <EditorSidebar
        onShowSettings={() => setShowSettingsModal(true)}
        isDarkMode={isDarkMode}
        onToggleDarkMode={onToggleDarkMode}
        onNavigateHome={handleNavigateHome}
        onCollapseSidebar={onToggleSidebar}
        collapsed={sidebarCollapsed}
        search={search}
      />

      {/* 侧栏收起后**以按钮形式显示**：左侧窄栏里就是"展开侧栏"按钮，
          外加"当前第几卷第几章"的分卷/章节数字导航（收起后基础导航仍然可用）。
          原先那个横跨侧栏整个高度的细长拖拽条（`.panel-toggle`）已按用户要求移除。 */}
      {sidebarCollapsed && <SidebarRail onExpand={onToggleSidebar} />}

      {/* 侧栏边缘拖拽调宽（收起时不显示：宽度已无意义） */}
      {!sidebarCollapsed && (
        <ResizeHandle
          direction={1}
          value={layout.sidebar}
          min={MIN_SIDEBAR_PX}
          onResize={resizeSidebarBy}
          onReset={() => setSidebarWidth(260)}
          label="调整左侧栏宽度"
          className="pane-resize-handle--sidebar"
        />
      )}

      <div className="editor-body">
        <div className="editor-main">
          <div className="editor-content">
            <div className="editor-view-host" hidden={activeView !== 'chapters'}>
              <Workspace />
            </div>
            {/*
              AI 对话视图：**一旦打开过就常驻**，之后只切 `hidden`，不再条件渲染。
              原因与右侧面板同一条：条件渲染 = 卸载组件，而 AI 回复的流式生成、
              活动流、编辑器锁都在组件内部（`ChatMode` 的回合状态），
              切走再切回就全丢了 —— 用户明确要求"切回章节时 AI 生成不能中断"。
              首次打开前不渲染，是为了不放弃 `lazy()` 带来的代码分割（不必在进创作页时
              就把整个对话页的 chunk 拉下来）。
            */}
            {chatEverOpened && (
              <div className="editor-view-host" hidden={activeView !== 'ai-chat'}>
                <Suspense fallback={null}>
                  <AIChatContent convFilterIds={convFilterIds} />
                </Suspense>
              </div>
            )}
            {activeView === 'version-history' && (
              <div className="editor-view-host">
                <Suspense fallback={null}>
                  <VersionHistoryPage onBack={() => setActiveView('chapters')} />
                </Suspense>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* 右侧区域：**一次只显示一页**（模型见 paneLayout.ts）。
          两处细节是这个区域的关键：

          1. **AI 助手只隐藏、从不卸载**：它一旦打开过就一直在（`key="ai"` 固定 DOM 身份），
             切走时宽度动画到 0 —— 否则正在跑的回合、活动流、编辑器锁全丢。
             其它页是数据视图，关掉就是真关掉（下次打开重新拉数据）。
          2. **退场也走动画**：关闭的那一页会多留 `PANE_ANIM_MS`，让宽度过渡到 0 再卸载。 */}
      <div className={`ws-region${aiOpen || renderedDataPane || aiMounted ? '' : ' is-empty'}`}>
        {/* ⚠️ 顺序就是视觉顺序：**AI 在数据页左侧**（用户要求"AI 面板永远在左侧"）。
            于是"打开 AI"看上去是从已打开的数据页左边继续向左伸出。 */}
        <div className="ws-columns">
          {aiMounted && (
            <section
              key="ai"
              className="ws-pane is-open"
              data-pane="ai"
              // AI 只隐藏：宽度归零 + aria-hidden，但**不卸载**
              aria-hidden={!aiOpen}
              style={{ '--ws-pane-width': `${aiCssWidth}px` } as React.CSSProperties}
              aria-label={PANE_LABEL.ai}
            >
              {/* ⚠️ 手柄**始终**渲染（不再因为"已被自动压窄"就藏起来）。
                  踩过：把那两处藏起来之后，一旦 `paneWidth` 被拖得过宽、而窗口又挤不下
                  （比如最大化时两块面板同时在窗口内），用户就**连拖窄都做不到**了 ——
                  手柄不见了，唯一的补救手段被自己藏掉了。
                  现在的做法是：手柄照常显示，但**从当前生效宽度开始拖**（见下面的 onResize），
                  于是"往回拖"立刻生效、能把过宽的值拉回合理范围；"往外拖"被钳制，
                  由 label 里的提示说明原因。 */}
              {aiOpen && (
                <ResizeHandle
                  direction={-1}
                  value={fit.pane}
                  min={MIN_PANE_PX}
                  // 从**生效宽度**起算：被钳制时用户往回拖能立刻见效（用 layout.paneWidth 会因为
                  // 起点过高而"拖很久才开始动"，等于没反应）
                  onResize={(delta) => resizePaneBy(delta)}
                  onReset={() => setPaneWidth(420)}
                  label={paneWidthClamped
                    ? `调整「${PANE_LABEL.ai}」宽度（窗口放不下 ${layout.paneWidth}px，已自动收窄到 ${fit.pane}px）`
                    : `调整「${PANE_LABEL.ai}」宽度`}
                  className="pane-resize-handle--inside"
                />
              )}
              {/* 内层固定宽度 + 右对齐：收起动画期间内容**被裁掉**而不是被挤扁（见 editor.css）。
                  宽度用**有效宽度**（视口挤不下时它就是被压过的值），否则裁剪会按错误宽度算。 */}
              <div className="ws-pane-body">
                <div className="ws-pane-fixed" style={{ width: `${fit.pane}px` }}>{paneBody.ai}</div>
              </div>
            </section>
          )}

          {/* 数据页：网页端在窗口内（AI 右侧）；桌面端在吸附窗口里，这块不渲染。
              退场期间 `renderedDataPane` 还是上一页，但宽度已归零（CSS 过渡收回）。 */}
          {dataInWindow && renderedDataPane && (
            <section
              key="data"
              className="ws-pane is-open"
              data-pane={renderedDataPane}
              style={{ '--ws-pane-width': `${dataCssWidth}px` } as React.CSSProperties}
              aria-label={PANE_LABEL[renderedDataPane]}
            >
              {/* 同 AI 面板：手柄始终显示、从生效宽度起算（见上面那段说明） */}
              {dataVisible && (
                <ResizeHandle
                  direction={-1}
                  value={fit.pane}
                  min={MIN_PANE_PX}
                  onResize={(delta) => resizePaneBy(delta)}
                  onReset={() => setPaneWidth(380)}
                  label={paneWidthClamped
                    ? `调整「${PANE_LABEL[renderedDataPane]}」宽度（窗口放不下 ${layout.paneWidth}px，已自动收窄到 ${fit.pane}px）`
                    : `调整「${PANE_LABEL[renderedDataPane]}」宽度`}
                  className="pane-resize-handle--inside"
                />
              )}
              {/* 内层固定宽度 + 右对齐：收起动画期间内容**被裁掉**而不是被挤扁（见 editor.css）。
                  宽度用**有效宽度**（视口挤不下时它就是被压过的值），否则裁剪会按错误宽度算。 */}
              <div className="ws-pane-body">
                <div className="ws-pane-fixed" style={{ width: `${fit.pane}px` }}>
                  {paneBody[renderedDataPane]}
                </div>
              </div>
            </section>
          )}
        </div>
      </div>

      {/* AI 在窗口内最左、数据页在它右侧（网页端）或在吸附窗口里（桌面端）—— 分派见 togglePaneById */}
      <PaneRail
        aiOpen={aiOpen}
        dataPane={dataPane}
        dataInWindow={dataInWindow}
        satelliteOnLeft={satelliteOnLeft}
        onToggle={togglePaneById}
        onSwapSide={isDesktopHost() ? swapSatelliteSide : undefined}
      />


      {showSettingsModal && <APISettingsModal onClose={() => setShowSettingsModal(false)} onResetWidths={resetWidths} />}


    </div>
  );
}
